import type { AssistantContext } from '../types'

export const AI_CONTEXT_MAX_BYTES = 8 * 1024

type AiRequestContext = Pick<
  AssistantContext,
  'courseId' | 'moduleId' | 'sectionId' | 'sectionText' | 'exerciseId' | 'userAttempt' | 'structuredCalculatorContext'
> & { courseVersion: string }

function byteLength(value: unknown): number {
  return new TextEncoder().encode(JSON.stringify(value)).length
}

function fitText(context: AiRequestContext): AiRequestContext {
  if (!context.sectionText || byteLength(context) <= AI_CONTEXT_MAX_BYTES) return context
  const original = context.sectionText
  let low = 0
  let high = original.length
  let best = ''
  while (low <= high) {
    const middle = Math.floor((low + high) / 2)
    const candidate = `${original.slice(0, middle).trimEnd()}…`
    if (byteLength({ ...context, sectionText: candidate }) <= AI_CONTEXT_MAX_BYTES) {
      best = candidate
      low = middle + 1
    } else {
      high = middle - 1
    }
  }
  return best ? { ...context, sectionText: best } : { ...context, sectionText: undefined }
}

export function buildAiRequestContext(
  context: AssistantContext,
  sectionText: string | undefined,
  courseVersion: string,
): AiRequestContext {
  const requestContext: AiRequestContext = {
    courseVersion,
    courseId: context.courseId,
    moduleId: context.moduleId,
    sectionId: context.sectionId,
    sectionText: sectionText?.trim() || undefined,
    exerciseId: context.exerciseId,
    userAttempt: context.userAttempt,
    structuredCalculatorContext: context.structuredCalculatorContext,
  }
  const fitted = fitText(requestContext)
  if (byteLength(fitted) > AI_CONTEXT_MAX_BYTES) {
    throw new Error('The selected exercise or calculator context is too large to send. Reduce it and try again.')
  }
  return fitted
}

export function aiContextByteLength(context: AiRequestContext): number {
  return byteLength(context)
}
